<template>
    <div class="p-grid">
        <div class="p-col-12">
            <div class="card">
                <h2>Reporting</h2>

                <TabView :activeIndex.sync="activeTabIndex">
                    <TabPanel header="Custom Reports">
                        <DataTable :rowsPerPageOptions="[10, 25, 50, 100]" :globalFilterFields="['name']" :filters="reportFilters" :value="reportSnapshotData" :paginator="true" :rows="10" class="site__table" ref="reports_table" :selection.sync="selectedRows" :loading="loading" sortField="name" :sortOrder="1">
                            <template #header>
                                <div id="button_row">
                                    <Button type="button" icon="pi pi-trash" class="p-button-danger" label="Delete" :disabled="selectedRows.length ? false : true" @click.prevent="toggleDeleteMultipleModal" />
                                    <Button type="button" class="p-button-primary" icon="pi pi-plus" label="New Report" id="new_report" @click.prevent="toggleNewReportModal" />
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search" />
                                        <InputText v-model="reportFilters['global'].value" placeholder="Search Custom Reports" />
                                    </span>
                                </div>
                            </template>
                            <template #empty>
                                No custom reports found.
                            </template>
                            <template #loading>
                                Loading custom reports. Please wait.
                            </template>
                            <Column selectionMode="multiple" headerClass="table__button--header" bodyClass="table__button"></Column>
                            <Column field="name" header="Report Name" headerClass="report_name">
                                <template #body="slotProps">
                                    <span>{{ slotProps.data.name }}</span>
                                </template>
                            </Column>
                            <Column bodyClass="edit_col">
                                <template #body="slotProps">
                                    <div class="row__buttons">
                                        <Button class="p-button p-button-link" @click.prevent="viewReport(slotProps.data.report_id)">
                                            VIEW
                                        </Button>
                                        <Button type="button" class="p-button-danger" icon="pi pi-trash" @click="toggleDeleteModal(slotProps.data.report_id)" />
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Built-in Reports">
                        <DataTable :value="builtInReports" class="site__table" ref="built_in_reports_table" :loading="loading">
                            <template #empty>
                                No custom reports found.
                            </template>
                            <template #loading>
                                Loading custom reports. Please wait.
                            </template>
                            <Column field="name" header="Report Name" headerClass="report_name">
                                <template #body="slotProps">
                                    <span>{{ slotProps.data.name }}</span>
                                </template>
                            </Column>
                            <Column bodyClass="edit_col">
                                <template #body="slotProps">
                                    <div class="row__buttons">
                                        <Button class="p-button p-button-link" @click.prevent="viewBuiltInReport(slotProps.data.id)">
                                            DOWNLOAD
                                        </Button>
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Archive">
                        <DataTable :value="archiveReports" class="site__table archived__table" ref="archive_reports_table" :loading="loading" :paginator="true" :rows="10" :rowsPerPageOptions="[10, 25, 50, 100]" :filters="archiveFilters" :globalFilterFields="['name', 'created']">
                            <template #header>
                                <div id="button_row">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search" />
                                        <InputText v-model="archiveFilters['global'].value" placeholder="Search Archived Reports" />
                                    </span>
                                </div>
                            </template>
                            <template #empty>
                                No archived reports found.
                            </template>
                            <template #loading>
                                Loading archived reports. Please wait.
                            </template>
                            <Column field="created" header="Created At" headerClass="report_created" bodyClass="archived__report_created">
                                <template #body="slotProps">
                                    <span>{{ slotProps.data.created }}</span>
                                </template>
                            </Column>
                            <Column field="name" header="Report Name" headerClass="report_name" bodyClass="archived__report_name">
                                <template #body="slotProps">
                                    <strong>{{ slotProps.data.name }}</strong>
                                </template>
                            </Column>
                            <Column header="Download" headerClass="archived__download">
                                <template #body="slotProps">
                                    <div style="text-align: right;">
                                        <Button class="p-button p-button-link" @click.prevent="downloadArchiveReport(slotProps.data.url)">
                                            DOWNLOAD
                                        </Button>
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </TabPanel>
                </TabView>
            </div>
        </div>

        <Dialog :modal="true" :visible.sync="modalVisible" class="report__dialog" @hide="(viewingReport == true) ? clearReportStore() : null">
            <template #header>
                <div class="report__modal--header">
                    <small>Create Custom Report</small>
                    <InputText v-model="newCustomReportTitle" />
                </div>
            </template>

            <CreateCustomReportsModal @close-modal="toggleNewReportModal" @create-report="clearReportStore" :newReportName="newCustomReportTitle" :viewingReport="viewingReport" :reportId="viewingReportId" />
        </Dialog>

        <Dialog header="Delete Report?" :modal="true" :visible.sync="deleteWarningVisible">
            <p>Are you sure you want to delete this report? This action cannot be undone.</p>
            <Button type="button" class="p-button-danger" label="Delete" @click.prevent="deleteReport(selectedReportId)" />
        </Dialog>

        <Dialog header="Delete Selected Reports?" :modal="true" :visible.sync="deleteMultipleWarningVisible">
            <p>Are you sure you want to delete these reports? This action cannot be undone.</p>
            <Button type="button" class="p-button-danger" label="Delete" @click.prevent="deleteSelectedReports" />
        </Dialog>

        <Dialog :header="selectedBuiltInReport.name" :modal="true" :visible.sync="sendReportVisible" class="send_report__dialog">
            <div class="chart_filter">
                <MultiSelect v-model="builtInReportSelectedSites" :options="sites" :filter="true" optionLabel="name" placeholder="Select Sites" class="p-mb-2 p-mt-2" style="width: 30rem;" />
            </div>
            <div id="report__format" class="built_in_report__format">
                <span>Format Required:</span>
                <div>
                    <RadioButton id="reportFormatCSV" name="reportFormatCSV" value="csv" v-model="reportFormat"></RadioButton>
                    <label for="reportFormatCSV">CSV</label>
                </div>
                <div>
                    <RadioButton id="reportFormatPDF" name="reportFormatPDF" value="pdf" v-model="reportFormat"></RadioButton>
                    <label for="reportFormatPDF">PDF</label>
                </div>
                <div>
                    <RadioButton id="reportFormatExcel" name="reportFormatExcel" value="xlxs" v-model="reportFormat"></RadioButton>
                    <label for="reportFormatExcel">Excel</label>
                </div>
            </div>
            <!-- <div id="report__emails" class="built_in_report__emails">
                <div>
                    <label for="emailMain">Send to:</label>
                    <Chips name="emailMain" v-model="emailMain" separator="," addOnBlur />
                </div>

                <div>
                    <label for="emailCC">CC:</label>
                    <Chips name="emailCC" v-model="emailCC" separator="," addOnBlur />
                </div>
            </div> -->
            <Button type="button" class="p-button" label="Download Report" @click.prevent="sendBuiltInReport" :icon="builtInLoading ? 'pi pi-spin pi-spinner' : ''" iconPos="right" :disabled="builtInLoading" />
        </Dialog>

    </div>
</template>

<script>
import { db } from '@/firebase.js';
import { FilterMatchMode } from 'primevue/api';
import CreateCustomReportsModal from '../components/CreateCustomReportsModal.vue';
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
    components: {
        CreateCustomReportsModal
    },
    data() {
        return {
            loading: true,
            reportSnapshotData: [],
            selectedRows: [],
            modalVisible: false,
            modalLoading: false,
            newCustomReportTitle: '',
            deleteWarningVisible: false,
            deleteMultipleWarningVisible: false,
            selectedReportId: null,
            reportFilters: { global: { value: null, matchMode: FilterMatchMode.CONTAINS } },
            viewingReport: false,
            viewingReportId: null,

            builtInReports: [
                { name: 'Asset Maintenance Schedule - Dashboard Report', id: 'asset_maintenance_schedule' },
                { name: 'Location Attendance Schedule - Dashboard Report', id: 'location_attendance_schedule' },
                { name: 'Asset/Location Maintenance Status - Dashboard Report', id: 'asset_location_maintenance_status' },
                { name: 'Asset/Location Fault Report Status - Dashboard Report', id: 'asset_location_fault_report_status' },
                { name: 'Asset Warranty Status - Dashboard Report', id: 'asset_warranty_status' },
                { name: 'Asset Lifecycle Status - Dashboard Report', id: 'asset_lifecycle_status' },
            ],
            sendReportVisible: false,
            reportFormat: 'csv',
            selectedBuiltInReport: { name: '', id: '' },
            emailMain: [],
            emailCC: [],
            builtInLoading: false,
            archiveReports: [],
            activeTabIndex: 0,
            archiveFilters: { global: { value: null, matchMode: FilterMatchMode.CONTAINS } },
            builtInReportSelectedSites: [],
            sites: []
        }
    },
    mounted() {
        this.fetchReportData()
    },
    computed: {
        ...mapGetters({
            user: "user",
            connected: "connected"
        }),
    },
    watch: {
        activeTabIndex(newValue) {
            if (newValue === 2 && this.archiveReports.length === 0) {
                this.fetchArchivedReports()
            }
        },
        sendReportVisible(newValue) {
            if (newValue && this.sites.length == 0) {
                this.fetchSites()
            }
        }
    },
    methods: {
        async fetchReportData() {
            this.loading = true
            const reportSnapshot = await db.collection('custom_reports').where('client_id', '==', this.$route.params.client_id).get()

            for await (const report of reportSnapshot.docs) {
                let data = report.data()
                data.report_id = report.id
                this.reportSnapshotData.push(data)
            }

            this.setNewCustomReportTitle()

            this.loading = false
        },
        toggleNewReportModal() {
            this.modalVisible = !this.modalVisible
            this.setNewCustomReportTitle()
        },
        toggleDeleteModal(reportId) {
            this.selectedReportId = reportId;
            this.deleteWarningVisible = !this.deleteWarningVisible;
        },
        setNewCustomReportTitle() {
            this.newCustomReportTitle = `Custom Report ${this.reportSnapshotData.length + 1}`
        },
        clearReportStore() {
            this.reportSnapshotData = []
            this.viewingReport = false
            this.fetchReportData()
            this.$store.dispatch('createCustomReport/setSites', [])
            this.$store.dispatch('createCustomReport/selectReportType', 0)
            this.$store.dispatch('createCustomReport/setAssetTypes', [])
            this.$store.dispatch('createCustomReport/selectFilterOptions', {})
            this.$store.dispatch('createCustomReport/selectReportDetails', [])
            this.$store.dispatch('createCustomReport/setSchedulingDetails', {})
            this.$store.dispatch('createCustomReport/selectMaintenanceAssetType', 'Asset')
            this.$store.dispatch('createCustomReport/setCustomRangeValue', null)
            this.$store.dispatch('createCustomReport/setCustomRangeInterval', 'days')
            this.$store.dispatch('createCustomReport/setFaultTypes', [])
        },
        async deleteReport(reportId) {
            try {
                const reportDoc = await db.collection('custom_reports').doc(reportId).get();
                const reportData = reportDoc.data();

                // Move report to bin collection
                await db.collection('report_bin').doc(reportId).set({
                    metadata: reportData,
                    deleted_timestamp: new Date(),
                    client_id: this.$route.params.client_id
                });

                console.log('added to bin')

                // Delete the original report
                await db.collection('custom_reports').doc(reportId).delete();

                // Log the deletion
                await db.collection('logs').add({
                    activity: 'Report Deleted',
                    timestamp: new Date(),
                    user: this.user.data.email,
                    client_id: this.user.data.client.key
                });

                this.reportSnapshotData = [];
                this.fetchReportData();
                this.deleteWarningVisible = false;
                this.selectedReportId = null;
                this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Report deleted successfully', life: 3000 });
            } catch (error) {
                console.error('Error deleting report:', error);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete report', life: 3000 });
            }
        },
        toggleDeleteMultipleModal() {
            this.deleteMultipleWarningVisible = !this.deleteMultipleWarningVisible
        },
        async deleteSelectedReports() {
            try {
                const batch = db.batch();
                const binBatch = db.batch();

                // Prepare all operations in batches
                for (const report of this.selectedRows) {
                    const reportRef = db.collection('custom_reports').doc(report.report_id);
                    const binRef = db.collection('report_bin').doc(report.report_id);

                    // Get the report data
                    const reportDoc = await reportRef.get();
                    const reportData = reportDoc.data();

                    // Add to bin batch
                    binBatch.set(binRef, {
                        metadata: reportData,
                        deleted_timestamp: new Date(),
                        client_id: this.$route.params.client_id
                    });

                    // Add to delete batch
                    batch.delete(reportRef);
                }

                // Commit both batches
                await binBatch.commit();
                await batch.commit();

                // Log the bulk deletion
                await db.collection('logs').add({
                    activity: 'Reports Deleted',
                    timestamp: new Date(),
                    user: this.user.data.email,
                    client_id: this.user.data.client.key,
                    count: this.selectedRows.length
                });

                this.reportSnapshotData = [];
                this.fetchReportData();
                this.deleteMultipleWarningVisible = false;
                this.selectedRows = [];
                this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Reports deleted successfully', life: 3000 });
            } catch (error) {
                console.error('Error deleting reports:', error);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete reports', life: 3000 });
            }
        },
        async viewReport(reportId) {
            try {
                this.viewingReportId = reportId
                const reportDoc = await db.collection('custom_reports').doc(reportId).get();
                const reportData = reportDoc.data();

                // Set the report name
                this.newCustomReportTitle = reportData.name;

                // Fetch complete site data
                const sitesPromises = reportData.sites.map(siteId =>
                    db.collection('sites').doc(siteId).get()
                );
                const sitesSnapshots = await Promise.all(sitesPromises);
                const formattedSites = sitesSnapshots.map(snapshot => ({
                    site_id: snapshot.id,
                    site_name: snapshot.data().name
                }));

                // Format asset types using the same structure as CustomReportPage1
                const formattedAssetTypes = reportData.assetTypes.map(type => ({
                    asset_type: type,
                    label: type
                }));

                const fault_statuses = [
                    { label: 'Fault', value: 'active', hex: '#f31b2b' },
                    { label: 'In Progress', value: 'in_progress', hex: '#ffbf00' },
                    { label: 'Rectified', value: 'rectified', hex: '#1f5ea4' }
                ]

                Object.keys(reportData.assetFilterOptions).forEach(filterOption => {
                    if (filterOption == 'fault_status') {
                        reportData.assetFilterOptions[filterOption].forEach((item, index) => {
                            item = fault_statuses.find(
                                status => status.value === item
                            ).label
                            reportData.assetFilterOptions[filterOption][index] = item
                        });
                    }

                    if (filterOption != 'fault_status' && filterOption.includes('_status')) {
                        console.log(reportData.assetFilterOptions[filterOption])

                        reportData.assetFilterOptions[filterOption].forEach((item, index) => {
                            item = item.asset_page_label
                            reportData.assetFilterOptions[filterOption][index] = item
                        });
                    }
                });

                // Load data into store
                this.$store.dispatch('createCustomReport/setSites', formattedSites);
                this.$store.dispatch('createCustomReport/selectReportType', reportData.reportType);
                this.$store.dispatch('createCustomReport/setAssetTypes', formattedAssetTypes);
                this.$store.dispatch('createCustomReport/selectFilterOptions', reportData.assetFilterOptions);
                this.$store.dispatch('createCustomReport/setSchedulingDetails', reportData.schedulingMeta);
                this.$store.dispatch('createCustomReport/selectMaintenanceAssetType', reportData.maintenanceAssetType);
                this.$store.dispatch('createCustomReport/setCustomRangeValue', reportData.customRangeValue);
                this.$store.dispatch('createCustomReport/setCustomRangeInterval', reportData.customRangeInterval);
                this.$store.dispatch('createCustomReport/selectRange', reportData.selectedRange);
                this.$store.dispatch('createCustomReport/setFaultTypes', reportData.fault_data)
                
                setTimeout(() => { // nmeeds to be delayed to avoid the watcher on page 1
                    this.$store.dispatch('createCustomReport/selectReportDetails', reportData.reportDetails);
                    // TODO: Add other store actions to reset the TODO report types
                }, 100)

                this.viewingReport = true;
                this.modalVisible = true;
            } catch (error) {
                console.error('Error loading report:', error);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load report', life: 3000 });
            }
        },

        viewBuiltInReport(reportId) {
            this.sendReportVisible = true
            this.selectedBuiltInReport = this.builtInReports.find(report => report.id == reportId)
        },

        async sendBuiltInReport() {
            let requestUrl
            const sites = (this.builtInReportSelectedSites.length) ? this.builtInReportSelectedSites.map(site => site.key).join(",") : this.sites.map(site => site.key).join(",")

            this.builtInLoading = true

            switch (this.reportFormat) {
                case 'pdf':
                    requestUrl = `${process.env.VUE_APP_API_URL}/export/pdf/${this.selectedBuiltInReport.id}`
                    break;
                default:
                    requestUrl = `${process.env.VUE_APP_API_URL}/export/${this.selectedBuiltInReport.id}`
                    break;
            }

            axios({
                method: 'get',
                url: requestUrl,
                headers: {
                    'Authorization': `Bearer ${this.user.auth.accessToken}`
                },
                params: {
                    clientId: this.$route.params.client_id,
                    selectedSites: sites,
                    isExcel: this.reportFormat === 'xlxs'
                }
            }).then((response) => {

                db.collection('logs').add({
                    activity: 'New Built-in Report Generated: ' + this.selectedBuiltInReport.name,
                    timestamp: new Date(),
                    user: this.user.data.email,
                    client_id: this.user.data.client.key
                }).then(() => {
                    if (this.reportFormat === 'pdf') {
                        // Open PDF in new tab
                        window.open(response.data.URL, '_blank');
                    } else {
                        // Handle CSV download
                        if (response.data.message) {
                            this.$toast.add({ severity: 'error', summary: 'Error', detail: response.data.message, life: 3000 })
                        } else {
                            const anchor = document.createElement('a');
                            anchor.href = response.data.URL;
                            anchor.setAttribute('download', response.data.URL);
                            document.body.appendChild(anchor);
                            anchor.click();
                            document.body.removeChild(anchor);
                        }
                    }
                    this.builtInLoading = false;
                    this.sendReportVisible = false
                    if (!response.data.message) {
                        this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Report created successfully', life: 3000 })
                    }
                });
            }).catch(error => {
                console.log(error)
                this.$toast.add({ severity: 'error', summary: 'Error', detail: error, life: 3000 })
                this.builtInLoading = false;
            })

        },

        async fetchArchivedReports() {
            this.loading = true
            axios({
                method: 'post',
                url: `${process.env.VUE_APP_API_URL}/reports/archive`,
                headers: {
                    'Authorization': `Bearer ${this.user.auth.accessToken}`
                },
                data: {
                    clientId: this.$route.params.client_id
                }
            }).then((response) => {
                this.archiveReports = response.data.files.map(file => ({
                    name: file.name.split('/').pop(),
                    created: new Date(file.created).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' @ ' + new Date(file.created).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }),
                    url: file.url
                }))
                this.loading = false
            }).catch((error) => {
                console.log(error)
                this.loading = false
            })
        },

        async fetchSites() {
            axios({
                method: 'post',
                url: process.env.VUE_APP_API_URL + '/dashboard/init/sites', // same as dashboard init
                headers: {
                    'Authorization': `Bearer ${this.user.auth.accessToken}`
                },
                data: {
                    'client': this.$route.params.client_id
                }
            }).then((response) => {
                this.sites = response.data;
                console.log(this.sites)
            }).catch(error => {
                this.$toast.add({ severity: 'error', summary: 'Error', detail: error, life: 3000 })
            });
        },

        downloadArchiveReport(url) {
            const anchor = document.createElement('a');
            anchor.target = '_blank';
            anchor.href = url;
            anchor.setAttribute('download', url);
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        }
    }
}
</script>

<style lang="scss">
#button_row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
}

.row__buttons {
    display: flex;
    gap: 0.5rem;
}

.site__table {
    min-width: 60vw;

    .p-datatable-header {
        // background: color-mix(in srgb, var(--primary-button-color) 5%, white) !important;
        background-color: white !important;
    }

    .p-datatable-wrapper {
        overflow-x: hidden;
    }

    &.p-datatable {
        .p-datatable-header {
            border: 0;
        }

        .p-datatable-tbody {
            tr:nth-of-type(odd) {
                background: color-mix(in srgb, var(--primary-button-color) 5%, white) !important;
            }

            tr>td {
                border: none;
            }
        }

        .p-datatable-thead {
            display: none;
        }

        .p-datatable-thead>tr>th {
            background-color: color-mix(in srgb, var(--primary-button-color) 5%, transparent) !important;
            border: 0;

            &:last-of-type:before {
                left: 0;
            }
        }
    }

    .edit_col {
        width: 6rem;

        a {
            text-align: right;
            display: block;
        }
    }

    &.p-datatable {
        .p-datatable-tbody>tr {
            transition: all 0.1s ease-in;

            &.p-highlight {
                background-color: #afafaf33 !important;
            }
        }
    }

    .p-highlight:not(.p-paginator-page) {
        &.p-checkbox-box {
            background-color: initial !important;
            color: initial !important;
        }

        .p-checkbox-box .p-checkbox-icon,
        &.p-checkbox-box .p-checkbox-icon {
            color: var(--link-text-color) !important;
        }

        .edit_link {
            color: #0388e5 !important;
        }
    }
}

.report__modal--header {
    display: grid;
    gap: 0.5rem;
    width: 100%;

    .p-inputtext {
        font-size: 20px;
        width: 100%;
        max-width: 300px;
    }
}

.report__dialog {
    .p-dialog {
        width: 80vw;
        height: 80vh;
        overflow: hidden;
    }

    .p-dialog-header {
        background-color: #edf0f5;
    }

    .p-dialog-content {
        padding: 0;
        flex-grow: 1;
        overflow-x: hidden;
        // display: flex;

        >div {
            min-height: calc(100% - 61px);
        }

        >div,
        .report__page {
            // height: 100%;
            width: 100%;
            margin-bottom: calc(61px);

            .button__row {
                margin-top: auto;
                position: fixed;
                bottom: 0;
                width: 100%;
                background-color: white;
                box-shadow: -2px -2px 11px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
            }
        }

        .report__page {
            display: flex;
            flex-direction: column;
        }
    }
}

.send_report__dialog {
    .p-dialog-content {
        // min-height: 400px;
        overflow: visible;
    }
}

.report__tabs {
    .p-tabview-panels {
        padding: 0;
    }

    .p-tabview-nav-content {
        .p-disabled {
            display: none !important;
        }
    }
}

#report__format {
    display: flex;
    gap: 1.5rem;
    padding-inline: 1.5rem;
    margin-top: 1.5rem;

    >div {
        display: flex;
        gap: 0.25rem;
    }
}

.send_report__dialog .p-dialog {
    overflow: visible;

    #report__format {
        &.built_in_report__format {
            padding: 0;
            padding-block: 0.5rem;
            margin: 0;
            margin-bottom: 1rem;
        }
    }

    #report__emails {
        &.built_in_report__emails {
            padding: 0;
            margin: 0;
            margin-bottom: 3rem;
        }
    }

    .p-dialog {
        width: 40vw;
        min-width: 400px;
        overflow: hidden;
    }
}

.archived__report_name {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>